import type { ReactNode } from 'react'
import { useI18n } from '../../i18n/I18nContext'
import { stateHeadlines } from '../../i18n/stateHeadlines'
import PaintedHeadline from './PaintedHeadline'
import PublicStateShell from '../public/PublicStateShell'
import type { ErrorKind } from '../../lib/friendlyError'
import { Button, ButtonLink } from './Primitives'

interface LoadErrorStateProps {
    /** What actually went wrong — decides the copy, icon, and default action. */
    kind: ErrorKind
    /** Re-run the failed load. Used for the transient kinds (offline/server/unknown). */
    onRetry?: () => void
    /** `public` wraps the public header + card shell; `app` is a plain centred block. */
    variant?: 'public' | 'app'
    /** Override the default action button (e.g. a class page's "Browse classes"). */
    action?: ReactNode
    /** Contextual app/catalog states use h2 beneath their page heading. */
    headingLevel?: 'h1' | 'h2'
    /** Catalog supplies its own page chrome and keeps failure copy beside discovery. */
    presentation?: 'default' | 'catalog'
}

// Two or three painted words per failure, the page's picture (founder, 9 Oct 2026).


/**
 * A full-page load-failure screen that shows the RIGHT message for the RIGHT
 * failure — a dropped network never reads as "not found". Transient failures
 * (offline / server / unknown) get a Retry; a genuine 404 / permission issue
 * gets a navigation action instead.
 */
export default function LoadErrorState({
    kind,
    onRetry,
    variant = 'app',
    action,
    headingLevel = 'h1',
    presentation = 'default',
}: LoadErrorStateProps) {
    const { t, language } = useI18n()

    // One sentence per failure on screen (founder, 7 Oct 2026); the body lines are not shown.
    const copy: Record<ErrorKind, { title: string; body: string }> = {
        offline: { title: t('errorPage.offline.title'), body: t('errorPage.offline.body') },
        server: { title: t('errorPage.serverError.title'), body: t('errorPage.serverError.body') },
        notfound: { title: t('errorPage.notFound.title'), body: t('errorPage.notFound.body') },
        auth: { title: t('errorPage.sessionExpired.title'), body: t('errorPage.sessionExpired.body') },
        forbidden: { title: t('errorPage.forbidden.title'), body: t('errorPage.forbidden.body') },
        unknown: { title: t('errorPage.serverError.title'), body: t('errorPage.serverError.body') },
    }

    const { title } = copy[kind]
    const painted = stateHeadlines(language)
    const headline = kind === 'offline' ? painted.offline : kind === 'notfound' ? painted.notFound
        : kind === 'auth' ? t('errorPage.sessionExpired.title') : kind === 'forbidden' ? t('errorPage.forbidden.title') : painted.error
    const canRetry = kind === 'offline' || kind === 'server' || kind === 'unknown'

    const defaultAction =
        kind === 'auth' ? (
            <ButtonLink href="/login">
                {t('errorPage.sessionExpired.action')}
            </ButtonLink>
        ) : canRetry ? (
            <Button onClick={() => (onRetry ? onRetry() : window.location.reload())}>
                {t('errorPage.tryAgain')}
            </Button>
        ) : (
            <ButtonLink href="/">
                {t('errorPage.notFound.goHome')}
            </ButtonLink>
        )

    const content = action ?? defaultAction
    const Heading = headingLevel

    if (variant === 'public' && presentation !== 'catalog') {
        return (
            <PublicStateShell title={title} headline={headline} alert>
                {content}
            </PublicStateShell>
        )
    }

    return (
        <div
            role="alert"
            className={`flex flex-col items-center justify-center px-6 text-center text-[var(--dhtn-text)] ${presentation === 'catalog' ? 'min-h-[320px]' : 'min-h-[70vh]'}`}
        >
            {presentation !== 'catalog' ? <>
                <div className="w-full max-w-2xl"><PaintedHeadline text={headline} size="inline" as={Heading} /></div>
                <p className="mb-7 mt-4 max-w-md text-base leading-7 text-[var(--dhtn-text-muted)]">{title}</p>
            </> : <Heading className="mb-7 font-heading text-2xl font-bold">{title}</Heading>}
            {content}
        </div>
    )
}
